// Explorer Risk Autopilot - Organizational risk processing for Explorer assessments
// Uses Explorer services and Redux state (explorerRisks, not modelRisks)
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Card, Stack, Group, Text, Badge,
  RingProgress, Center, Box,
  Timeline, Divider, Alert, Button, Transition
} from '@mantine/core';
import {
  IconTarget, IconAnalyze, IconSearch,
  IconRefresh, IconShieldCheck, IconRocket,
  IconBrain, IconBuildingBank, IconUsers,
  IconAlertTriangle, IconCheck, IconHourglassHigh, IconChevronRight,
  IconTrendingUp, IconScale, IconLeaf, IconStar, IconShield
} from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import LiveTaskOutput from '../components/LiveTaskOutput';
// Explorer Services
import {
  ExplorerDiscoveryService,
  ExplorerEvaluationService,
  ExplorerActionPlanningService,
  ExplorerResidualRiskService
} from './services';
// RTK slice actions
import {
  selectInheritedBuilderRisks,
  EXPLORER_RISK_AREAS
} from '../redux/slices/risksSlice';
import {
  setAreaStatus,
  updateAreaProgress,
  saveCheckpoint,
  markResumed,
  clearCheckpoint,
  selectResumeData
} from '../redux/slices/assessmentProgressSlice';
import {
  fetchRiskCardData,
  setAssessmentCheckpoint
} from '../redux/slices/assessmentsSlice';
// Selectors
import { createImpactCategoriesSelector } from '../redux/selectors/riskFormGenerator';
import { selectContextBridge } from '../redux/selectors/assessmentSelectors';

// Explorer-specific task definitions (same phases as Builder but organizational focus)
const EXPLORER_TASKS = [
  {
    key: 'analyzing risk area',
    label: 'Analyzing Risk Impact',
    icon: IconAnalyze,
    color: '#7c3aed',
    description: 'Analyzing risk landscape and business context'
  },
  {
    key: 'researching risk sources',
    label: 'Researching Risk Sources',
    icon: IconSearch,
    color: '#339af0',
    description: 'Evidence extraction for risk sources'
  },
  {
    key: 'researching root causes',
    label: 'Researching Root Causes',
    icon: IconTarget,
    color: '#fd7e14',
    description: 'Research causes with business impact analysis'
  },
  {
    key: 'preparing causes for RAG',
    label: 'Preparing Causes for RAG',
    icon: IconBrain,
    color: '#20c997',
    description: 'Extracting relevant causes for enhanced scenario generation'
  },
  {
    key: 'scenarios',
    label: 'Generating Risk Scenarios',
    icon: IconAlertTriangle,
    color: '#e03131',
    description: 'Creating risk scenarios with RAG-enhanced analysis'
  },
  {
    key: 'evaluation',
    label: 'Evaluating Risk Priorities',
    icon: IconScale,
    color: '#9c36b5',
    description: 'Assessing risk priority using AI'
  },
  {
    key: 'actions',
    label: 'Planning Actions',
    icon: IconRocket,
    color: '#37b24d',
    description: 'Developing mitigation strategies'
  },
  {
    key: 'residual',
    label: 'Calculating Residual Risk',
    icon: IconShieldCheck,
    color: '#f76707',
    description: 'Determining remaining risk after controls'
  }
];

// Explorer area labels (organizational focus)
const EXPLORER_AREA_LABELS = {
  commercialRisk: { label: 'Commercial Risk', icon: IconTrendingUp, color: '#339af0' },
  operationalRisk: { label: 'Operational Risk', icon: IconBuildingBank, color: '#fd7e14' },
  complianceRisk: { label: 'Compliance & Legal', icon: IconScale, color: '#7c3aed' },
  societalRisk: { label: 'Societal & Ethical', icon: IconUsers, color: '#e03131' },
  environmentalRisk: { label: 'Environmental', icon: IconLeaf, color: '#37b24d' },
  reputationalRisk: { label: 'Reputational', icon: IconStar, color: '#f76707' },
  securityImpact: { label: 'Security Impact', icon: IconShield, color: '#c92a2a' }
};

const progressForPhase = (phase) => {
  switch (phase) {
    case 'discovery': return 25;
    case 'evaluation': return 50;
    case 'actions': return 75;
    case 'residual': return 90;
    case 'completed': return 100;
    default: return 5;
  }
};

export default function ExplorerRiskAutopilot({
  modelId,
  assessmentUuid,
  parentAssessmentUuid, // Builder assessment UUID for inherited risks
  businessContext,
  approvedNarrative,
  onComplete,
  onAreaProgress,
  autonomous = false
}) {
  const dispatch = useDispatch();
  const { user } = useAuth0();

  // Resume position from Redux
  const resumeFromPosition = useSelector((state) => selectResumeData(state, assessmentUuid));

  // Assessment state
  const assessmentEntry = useSelector((s) => assessmentUuid ? s.assessments?.byId?.[assessmentUuid] : null);
  const contextData = useSelector(selectContextBridge(assessmentUuid, modelId));
  const riskCauseEvalCfg = useSelector((s) => s.config?.configs?.riskCauseEvaluation);

  // Inherited Builder risks for context
  const inheritedRisks = useSelector((state) => {
    if (!parentAssessmentUuid) return [];
    return selectInheritedBuilderRisks(state, parentAssessmentUuid);
  });

  // Impact categories from template
  const impactCategoriesSelector = useMemo(() => createImpactCategoriesSelector(assessmentUuid), [assessmentUuid]);
  const impactCategories = useSelector(impactCategoriesSelector);

  // Template config
  const templateConfig = assessmentEntry?.templateConfig || {};

  // Refs
  const autonomousRef = useRef(autonomous);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    autonomousRef.current = autonomous;
  }, [autonomous]);

  // Local state
  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [statusByArea, setStatusByArea] = useState({});
  const [areaOutputs, setAreaOutputs] = useState({});
  const [error, setError] = useState(null);
  const [waitingForDecision, setWaitingForDecision] = useState(false);
  const [completedAreaData, setCompletedAreaData] = useState(null);

  // Explorer uses 6 organizational areas (no Security)
  const areas = EXPLORER_RISK_AREAS;

  // Live update callback
  const handleLiveUpdate = useCallback((updateType, data) => {
    console.log(`[ExplorerLiveUpdate] ${updateType}:`, data);
    const areaKey = data.areaKey || areas[currentAreaIndex];

    setAreaOutputs((prev) => {
      const currentResults = prev[areaKey] || {};

      switch (updateType) {
        case 'area':
          return { ...prev, [areaKey]: { ...currentResults, areaCard: data.areaCard } };
        case 'sources':
          return { ...prev, [areaKey]: { ...currentResults, selectedSources: data.selectedSources || [] } };
        case 'causes':
          return { ...prev, [areaKey]: { ...currentResults, causesBySource: data.causesBySource || {} } };
        case 'causesForRAG':
          return { ...prev, [areaKey]: { ...currentResults, relevantCauses: data.relevantCauses || [] } };
        case 'scenarios':
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              scenarios: data.scenarios || [],
              ragMetadata: data.ragMetadata || {},
              scenarioStats: {
                totalGenerated: data.totalGenerated || 0,
                totalPromoted: data.totalPromoted || 0,
                generationMethod: data.ragMetadata?.method || 'Unknown'
              }
            }
          };
        case 'riskEvaluation':
          const evaluations = currentResults.riskEvaluations || [];
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              riskEvaluations: [...evaluations, data],
              evaluationProgress: `${data.evaluatedCount}/${data.totalRisks}`
            }
          };
        case 'controlsGenerated':
          const controls = currentResults.controlUpdates || [];
          return { ...prev, [areaKey]: { ...currentResults, controlUpdates: [...controls, data] } };
        case 'residualRiskCalculated':
          const residuals = currentResults.residualCalculations || [];
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              residualCalculations: [...residuals, data],
              residualProgress: `${data.assessedCount}/${data.totalRisks}`
            }
          };
        default:
          return prev;
      }
    });
  }, [areas, currentAreaIndex]);

  // Initialize services
  const discoveryService = useMemo(() => new ExplorerDiscoveryService(
    dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, handleLiveUpdate]);

  const evaluationService = useMemo(() => new ExplorerEvaluationService(
    dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate]);

  const actionService = useMemo(() => new ExplorerActionPlanningService(
    dispatch, modelId, assessmentUuid, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);

  const residualService = useMemo(() => new ExplorerResidualRiskService(
    dispatch, modelId, assessmentUuid, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);

  // Initialize queue
  useEffect(() => {
    const initKey = `${areas.join(',')}-${resumeFromPosition?.pausedAt || 'new'}`;
    if (hasInitializedRef.current === initKey || !areas.length) return;

    hasInitializedRef.current = initKey;

    setStatusByArea((prev) => {
      const next = { ...prev };
      const completedAreas = resumeFromPosition?.completedAreas || [];
      const resumeIdx = resumeFromPosition?.currentAreaIndex ?? 0;
      areas.forEach((area, index) => {
        if (!next[area]) {
          // Real resume semantics (parity with Builder): areas a prior run
          // finished are 'completed' so we don't redo them; the area we
          // paused at is 'queued'; everything after is 'waiting'.
          let status = 'waiting';
          if (completedAreas.includes(area)) {
            status = 'completed';
          } else if (index === resumeIdx) {
            status = 'queued';
          } else if (index < resumeIdx) {
            status = 'completed';
          }
          next[area] = {
            status,
            currentTask: null,
            completedTasks: [],
            startedAt: null,
            completedAt: null
          };
        }
      });
      return next;
    });

    if (resumeFromPosition?.currentAreaIndex >= 0) {
      dispatch(markResumed({ assessmentUuid }));
      setCurrentAreaIndex(resumeFromPosition.currentAreaIndex);
    } else {
      setCurrentAreaIndex(0);
    }
  }, [areas, resumeFromPosition, dispatch, assessmentUuid]);

  const riskNarrative = approvedNarrative || '';

  // Save checkpoint
  const saveProgressCheckpoint = useCallback(async (areaKey, areaIndex, phase = null) => {
    try {
      const completedAreas = areas.slice(0, areaIndex).filter(a =>
        statusByArea[a]?.status === 'completed'
      );

      const checkpoint = {
        majorStep: 'explorer-risks',
        currentArea: areaKey,
        currentAreaIndex: areaIndex,
        completedAreas,
        phase,
        narrativesApproved: true,
        assessmentType: 'explorer',
        pausedAt: new Date().toISOString()
      };

      dispatch(saveCheckpoint({ assessmentUuid, checkpoint }));
      await dispatch(setAssessmentCheckpoint({ modelId, assessmentUuid, checkpoint })).unwrap();

      const status = phase === 'completed' ? 'COMPLETE' : 'IN_PROGRESS';
      const progress = progressForPhase(phase);
      dispatch(setAreaStatus({ assessmentUuid, areaName: areaKey, status }));
      dispatch(updateAreaProgress({ assessmentUuid, areaName: areaKey, progress }));

      console.log(`[ExplorerRiskAutopilot] Checkpoint saved: ${areaKey} (${phase || 'start'})`);
    } catch (error) {
      console.error('[ExplorerRiskAutopilot] Failed to save checkpoint:', error);
      setError('Could not persist your progress. Please check your connection.');
    }
  }, [dispatch, modelId, assessmentUuid, areas, statusByArea]);

  // Runner effect
  useEffect(() => {
    const anyGenerating = Object.values(statusByArea).some((s) => s.status === 'generating');
    if (anyGenerating || waitingForDecision) return;

    const currentArea = areas[currentAreaIndex];
    if (!currentArea) {
      dispatch(clearCheckpoint({ assessmentUuid }));
      if (typeof onComplete === 'function') {
        onComplete();
      }
      return;
    }

    if (typeof onAreaProgress === 'function') {
      const completedCount = areas.filter(a => statusByArea[a]?.status === 'completed').length;
      onAreaProgress({
        currentArea: EXPLORER_AREA_LABELS[currentArea]?.label || currentArea,
        currentIndex: currentAreaIndex + 1,
        total: areas.length,
        completed: completedCount
      });
    }

    const areaStatus = statusByArea[currentArea]?.status;
    if (areaStatus === 'queued') {
      runAreaWithServices(currentArea);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusByArea, areas, currentAreaIndex, waitingForDecision]);

  async function runAreaWithServices(areaKey) {
    try {
      setError(null);

      setStatusByArea((prev) => ({
        ...prev,
        [areaKey]: {
          ...prev[areaKey],
          status: 'generating',
          currentTask: 'analyzing risk area',
          startedAt: new Date().toISOString()
        }
      }));

      // Get area definition from template
      const areaDefinition = templateConfig?.taxonomy?.riskAreas?.[areaKey] || {};

      console.log(`[ExplorerRiskAutopilot] Starting ${areaKey} with full service execution...`);

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'discovery');

      // Phase 1: Discovery
      let lastTask = null;
      const discoveryResults = await discoveryService.executeDiscovery(
        areaKey,
        areaDefinition,
        { riskNarrative, businessContext },
        riskCauseEvalCfg,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[ExplorerDiscovery] Results:`, discoveryResults);

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'evaluation');

      // Phase 2: Evaluation
      const evaluationResults = await evaluationService.executeEvaluation(
        areaKey,
        areaDefinition,
        templateConfig?.taxonomy,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[ExplorerEvaluation] Results:`, evaluationResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], evaluations: evaluationResults }
      }));

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'actions');

      // Phase 3: Action Planning
      const actionResults = await actionService.executeActionPlanning(
        areaKey,
        areaDefinition,
        contextData,
        templateConfig,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[ExplorerActions] Results:`, actionResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], actions: actionResults }
      }));

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'residual');

      // Phase 4: Residual Risk
      const residualResults = await residualService.executeResidualAssessment(
        areaKey,
        areaDefinition,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[ExplorerResidual] Results:`, residualResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], residual: residualResults }
      }));

      updateTaskProgress(areaKey, 'residual', null, true);
      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'completed');

      dispatch(fetchRiskCardData({ assessmentUuid, modelId }));

      const finalResults = {
        ...discoveryResults,
        evaluations: evaluationResults,
        actions: actionResults,
        residual: residualResults
      };
      setCompletedAreaData({
        areaKey,
        areaLabel: EXPLORER_AREA_LABELS[areaKey]?.label || areaKey,
        results: finalResults,
        isLastArea: currentAreaIndex === areas.length - 1
      });

      if (autonomousRef.current) {
        console.log(`[ExplorerRiskAutopilot] AUTONOMOUS: auto-continuing`);
        setWaitingForDecision(false);
        handleContinueToNext();
      } else {
        console.log(`[ExplorerRiskAutopilot] SUPERVISED: waiting for user`);
        setWaitingForDecision(true);
      }

    } catch (e) {
      setError(e.message);
      setStatusByArea((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], status: 'failed' }
      }));
    }
  }

  function updateTaskProgress(areaKey, currentTask, nextTask, completed = false) {
    setStatusByArea((prev) => {
      const areaStatus = prev[areaKey] || {};
      const completedTasks = [...(areaStatus.completedTasks || [])];

      if (currentTask && !completedTasks.includes(currentTask)) {
        completedTasks.push(currentTask);
      }

      return {
        ...prev,
        [areaKey]: {
          ...areaStatus,
          currentTask: completed ? null : nextTask,
          completedTasks,
          status: completed ? 'completed' : 'generating',
          completedAt: completed ? new Date().toISOString() : areaStatus.completedAt
        }
      };
    });
  }

  const handleContinueToNext = useCallback(() => {
    setWaitingForDecision(false);
    setCompletedAreaData(null);

    if (currentAreaIndex + 1 < areas.length) {
      const nextAreaIndex = currentAreaIndex + 1;
      const nextArea = areas[nextAreaIndex];

      setCurrentAreaIndex(nextAreaIndex);
      setStatusByArea(prev => ({
        ...prev,
        [nextArea]: { ...prev[nextArea], status: 'queued' }
      }));
    } else {
      console.log(`[ExplorerRiskAutopilot] All 6 Explorer areas complete`);
      if (typeof onComplete === 'function') {
        onComplete();
      }
    }
  }, [currentAreaIndex, areas, onComplete]);

  const handleSkipArea = useCallback((areaToSkip) => {
    setStatusByArea(prev => ({
      ...prev,
      [areaToSkip]: { ...prev[areaToSkip], status: 'skipped' }
    }));
    handleContinueToNext();
  }, [handleContinueToNext]);

  const handleGenerateReport = () => {
    setWaitingForDecision(false);
    setCompletedAreaData(null);
    if (typeof onComplete === 'function') {
      onComplete();
    }
  };

  const getTaskProgress = (areaKey) => {
    const areaStatus = statusByArea[areaKey] || {};
    const completedCount = areaStatus.completedTasks?.length || 0;
    return {
      completed: completedCount,
      total: EXPLORER_TASKS.length,
      percentage: Math.round((completedCount / EXPLORER_TASKS.length) * 100)
    };
  };

  const currentArea = areas[currentAreaIndex];
  const currentAreaLabel = EXPLORER_AREA_LABELS[currentArea]?.label || currentArea;

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertTriangle size="1rem" />}>
          {error}
        </Alert>
      )}

      <Transition mounted={!!currentArea} transition="fade" duration={400} timingFunction="ease-out">
        {(styles) => currentArea && (
          <div style={styles}>
            <ExplorerAreaCard
              areaKey={currentArea}
              areaLabel={currentAreaLabel}
              areaIndex={currentAreaIndex + 1}
              totalAreas={areas.length}
              status={statusByArea[currentArea] || {}}
              outputs={areaOutputs[currentArea]}
              progress={getTaskProgress(currentArea)}
              autonomous={autonomous}
              inheritedRisksCount={inheritedRisks.length}
              onRetry={() => setStatusByArea(prev => ({
                ...prev,
                [currentArea]: { ...prev[currentArea], status: 'queued' }
              }))}
              isWaitingForUserDecision={waitingForDecision}
              completedAreaData={completedAreaData}
              remainingAreas={areas.slice(currentAreaIndex + 1).map(a => EXPLORER_AREA_LABELS[a]?.label || a)}
              onContinue={handleContinueToNext}
              onSkip={() => handleSkipArea(currentArea)}
              onGenerateReport={handleGenerateReport}
            />
          </div>
        )}
      </Transition>
    </Stack>
  );
}

/**
 * Explorer Area Card - Shows organizational risk area processing progress
 */
function ExplorerAreaCard({
  areaKey,
  areaLabel,
  areaIndex,
  totalAreas,
  status,
  outputs,
  progress,
  autonomous,
  inheritedRisksCount,
  onRetry,
  isWaitingForUserDecision,
  completedAreaData,
  remainingAreas,
  onContinue,
  onSkip,
  onGenerateReport
}) {
  const isGenerating = status.status === 'generating';
  const isCompleted = status.status === 'completed';
  const isFailed = status.status === 'failed';
  const isQueued = status.status === 'queued';

  const areaConfig = EXPLORER_AREA_LABELS[areaKey] || { icon: IconAnalyze, color: '#1976d2' };
  const AreaIcon = areaConfig.icon;

  return (
    <Card
      withBorder
      p="lg"
      radius="md"
      style={{
        height: '100%',
        transition: 'all 0.2s ease',
        transform: isGenerating ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isGenerating ? '0 8px 25px rgba(25, 118, 210, 0.15)' : undefined,
        borderLeft: `4px solid ${areaConfig.color}`
      }}
    >
      <Stack gap="md" h="100%">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" style={{ flex: 1 }} align="flex-start">
            <AreaIcon size={24} color={areaConfig.color} />
            <div style={{ flex: 1 }}>
              <Group gap="xs" align="baseline">
                <Text fw={700} size="lg" lineClamp={2}>
                  {areaLabel}
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  ({areaIndex}/{totalAreas})
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                {isQueued && 'Waiting in queue...'}
                {isGenerating && 'Huginn is analyzing risks...'}
                {isCompleted && 'Risk analysis complete'}
                {isFailed && 'Analysis failed'}
              </Text>
              {inheritedRisksCount > 0 && (
                <Badge size="xs" variant="light" color="blue" mt={4}>
                  {inheritedRisksCount} inherited Builder risks
                </Badge>
              )}
            </div>
          </Group>

          <Badge
            variant="light"
            color={isCompleted ? 'green' : isGenerating ? 'blue' : isFailed ? 'red' : 'gray'}
            size="sm"
          >
            {isCompleted && <IconCheck size={14} />}
            {isGenerating && 'Processing'}
            {isFailed && <IconAlertTriangle size={14} />}
            {isQueued && <IconHourglassHigh size={14} />}
          </Badge>
        </Group>

        {/* Progress Ring */}
        <Center>
          <RingProgress
            size={120}
            thickness={8}
            sections={[
              {
                value: progress.percentage,
                color: isCompleted ? '#40c057' : isGenerating ? areaConfig.color : '#adb5bd'
              }
            ]}
            label={
              <Center>
                {isCompleted ? (
                  <IconCheck size={32} color="#40c057" />
                ) : isGenerating ? (
                  <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={28} />
                ) : (
                  <AreaIcon size={28} color="#adb5bd" />
                )}
              </Center>
            }
          />
        </Center>

        {/* Task Timeline */}
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={500} mb="xs">
            Risk Analysis Progress
          </Text>
          <Timeline active={progress.completed} bulletSize={24} lineWidth={2}>
            {EXPLORER_TASKS.map((task) => {
              const isActive = status.currentTask === task.key;
              const isTaskCompleted = status.completedTasks?.includes(task.key);

              return (
                <Timeline.Item
                  key={task.key}
                  bullet={
                    <Center style={{ width: 24, height: 24 }}>
                      {isTaskCompleted ? (
                        <IconCheck size={16} color="#40c057" />
                      ) : isActive ? (
                        <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={14} />
                      ) : (
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#adb5bd'
                          }}
                        />
                      )}
                    </Center>
                  }
                  title={
                    <Text
                      size="xs"
                      fw={isActive ? 600 : 400}
                      c={isTaskCompleted ? 'green' : isActive ? 'blue' : 'dimmed'}
                    >
                      {task.label}
                    </Text>
                  }
                >
                  <Text size="xs" c="dimmed">
                    {task.description}
                  </Text>
                  <LiveTaskOutput task={task.key} outputs={outputs} />
                  {isActive && (
                    <Box p="xs" bg="blue.0" style={{ borderRadius: 4 }} mt="xs">
                      <Group gap="xs" align="center">
                        <AIGeneratingIcon isGenerating size={12} />
                        <Text size="xs" c="blue">
                          Huginn is working on this task...
                        </Text>
                      </Group>
                    </Box>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Box>

        {/* Completion Summary */}
        {isCompleted && outputs && (
          <Box>
            <Divider my="sm" />
            <Text size="sm" fw={600} c="green" mb="xs">
              Risk Analysis Complete
            </Text>
            <Group gap="xs" mb="md">
              {outputs.scenarios?.length > 0 && (
                <Badge size="xs" variant="light" color="purple">
                  {outputs.scenarios.length} scenarios
                </Badge>
              )}
              {outputs.evaluations?.evaluatedRisks > 0 && (
                <Badge size="xs" variant="light" color="grape">
                  {outputs.evaluations.evaluatedRisks} priorities
                </Badge>
              )}
              {outputs.actions?.actionsGenerated > 0 && (
                <Badge size="xs" variant="light" color="orange">
                  {outputs.actions.actionsGenerated} controls
                </Badge>
              )}
              {outputs.residual?.residualAssessments > 0 && (
                <Badge size="xs" variant="light" color="teal">
                  {outputs.residual.residualAssessments} residual
                </Badge>
              )}
            </Group>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="center" gap="sm" mt="md">
          {isGenerating && (
            <Button
              size="sm"
              variant="light"
              color="orange"
              onClick={onSkip}
            >
              Skip Area
            </Button>
          )}

          {isCompleted && (
            <>
              <Text size="sm" fw={500} c="green" mr="md">
                Analysis Complete!
              </Text>

              {autonomous ? (
                <Group gap="xs" align="center">
                  <IconHourglassHigh size={16} color="#1976d2" />
                  <Text size="sm" c="blue">
                    {remainingAreas && remainingAreas.length > 0
                      ? `Continuing to ${remainingAreas[0]}...`
                      : 'Explorer assessment complete!'}
                  </Text>
                </Group>
              ) : (
                isWaitingForUserDecision && (
                  <>
                    {remainingAreas && remainingAreas.length > 0 ? (
                      <Button
                        size="md"
                        color="blue"
                        leftSection={<IconChevronRight size={18} />}
                        onClick={onContinue}
                      >
                        Continue to {remainingAreas[0]}
                      </Button>
                    ) : (
                      <Button
                        size="md"
                        color="grape"
                        leftSection={<IconCheck size={18} />}
                        onClick={onGenerateReport}
                      >
                        Generate Explorer Report
                      </Button>
                    )}
                  </>
                )
              )}
            </>
          )}

          {isFailed && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                leftSection={<IconRefresh size={14} />}
              >
                Retry Analysis
              </Button>
              <Button
                size="sm"
                variant="light"
                color="orange"
                onClick={onSkip}
              >
                Skip Area
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
